/**
 * Sitemap Generator for Global Therapy Hub
 * Generates comprehensive multilingual sitemap.xml for SEO
 * Reads city translations from Supabase database
 */

import { worldCities } from '@/data/cities';
import { citySpecialties } from '@/data/citySpecialties';
import { supabase } from '@/integrations/supabase/client';

export interface SitemapURL {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: string;
  images?: SitemapImage[];
  videos?: SitemapVideo[];
}

export interface SitemapImage {
  loc: string;
  title?: string;
  caption?: string;
  geoLocation?: string;
}

export interface SitemapVideo {
  thumbnailLoc: string;
  title: string;
  description: string;
  contentLoc?: string;
  playerLoc?: string;
  duration?: number;
}

export interface SitemapStats {
  totalURLs: number;
  cityLandings: number;
  specialtyLandings: number;
  staticPages: number;
  sitemapSize: number;
  estimatedIndexablePages: number;
}

const BASE_URL = 'https://psychotherapist.help';

// Supported languages for localized URLs
const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'ja', 'zh', 'ko', 'ar', 'hi', 'tr', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'bg', 'uk', 'el', 'he', 'th', 'vi', 'id', 'ms', 'fa', 'bn', 'ur', 'ta', 'te', 'mr', 'gu', 'kn', 'ml', 'si'];

/**
 * Fetch city translations from database
 */
const getCityTranslationsFromDB = async (): Promise<Array<{
  city_slug: string;
  language: string;
}>> => {
  const { data, error } = await supabase
    .from('city_translations')
    .select('city_slug, language');
  
  if (error) {
    console.error('❌ Error fetching translations:', error);
    return [];
  }
  
  console.log(`✅ Loaded ${data?.length || 0} city translations from database`);
  return data || [];
};

/**
 * Generate static pages URLs
 */
const generateStaticPages = (): SitemapURL[] => {
  const staticPages = [
    { path: '/', priority: '1.0', changefreq: 'daily' as const },
    { path: '/about', priority: '0.8', changefreq: 'monthly' as const },
    { path: '/find', priority: '0.9', changefreq: 'daily' as const },
    { path: '/specialties', priority: '0.9', changefreq: 'weekly' as const },
    { path: '/countries', priority: '0.9', changefreq: 'weekly' as const },
    { path: '/therapists', priority: '0.8', changefreq: 'daily' as const },
    { path: '/register', priority: '0.7', changefreq: 'monthly' as const },
    { path: '/login', priority: '0.5', changefreq: 'yearly' as const },
    { path: '/blog', priority: '0.7', changefreq: 'weekly' as const },
    { path: '/faq', priority: '0.6', changefreq: 'monthly' as const },
    { path: '/contact', priority: '0.6', changefreq: 'monthly' as const },
    { path: '/privacy', priority: '0.3', changefreq: 'yearly' as const },
    { path: '/terms', priority: '0.3', changefreq: 'yearly' as const }
  ];

  return staticPages.map(page => ({
    loc: `${BASE_URL}${page.path}`,
    lastmod: new Date().toISOString().split('T')[0],
    changefreq: page.changefreq,
    priority: page.priority
  }));
};

/**
 * Generate English city landing pages from cities table (32,720 cities)
 */
const generateEnglishCityLandingURLs = async (): Promise<SitemapURL[]> => {
  const today = new Date().toISOString().split('T')[0];
  const urls: SitemapURL[] = [];
  
  console.log(`🌍 Fetching all active cities from database...`);
  
  // Fetch all active cities from the cities table
  const { data: cities, error } = await supabase
    .from('cities')
    .select('name, slug, country')
    .eq('is_active', true)
    .order('name');
  
  if (error) {
    console.error('❌ Error fetching cities:', error);
    return urls;
  }
  
  console.log(`🌍 Generating English city landing URLs for ${cities?.length || 0} cities...`);
  
  cities?.forEach((city) => {
    urls.push({
      loc: `${BASE_URL}/city/${city.slug}`,
      lastmod: today,
      changefreq: 'weekly' as const,
      priority: '0.8',
      images: [{
        loc: `${BASE_URL}/images/cities/${city.slug}-hero.webp`,
        title: `Find Therapists in ${city.name}, ${city.country}`,
        geoLocation: `${city.name}, ${city.country}`
      }]
    });
  });
  
  console.log(`✅ Generated ${urls.length.toLocaleString()} English city landing URLs`);
  return urls;
};

/**
 * Generate translated city landing pages from city_translations table
 */
const generateTranslatedCityLandingURLs = async (): Promise<SitemapURL[]> => {
  const today = new Date().toISOString().split('T')[0];
  const urls: SitemapURL[] = [];
  const translations = await getCityTranslationsFromDB();
  
  console.log(`🌐 Generating translated city landing URLs for ${translations.length} translations...`);
  
  // Generate URLs for each translated city (exclude English as it's handled above)
  translations.forEach(({ city_slug, language }) => {
    if (language === 'en') return; // Skip English, handled by generateEnglishCityLandingURLs
    
    const city = worldCities.find(c => c.id === city_slug);
    if (!city) return;
    
    const url = `${BASE_URL}/${language}/city/${city_slug}`;
    
    urls.push({
      loc: url,
      lastmod: today,
      changefreq: 'weekly' as const,
      priority: '0.7',
      images: [{
        loc: `${BASE_URL}/images/cities/${city_slug}-hero.webp`,
        title: `Mental health services in ${city.name}`,
        geoLocation: city.name
      }]
    });
  });
  
  console.log(`✅ Generated ${urls.length.toLocaleString()} translated city landing URLs`);
  return urls;
};

/**
 * Generate English specialty sub-landing pages from cities table (32,720 × 20 = 654,400 URLs)
 */
const generateEnglishSpecialtyURLs = async (): Promise<SitemapURL[]> => {
  const today = new Date().toISOString().split('T')[0];
  const urls: SitemapURL[] = [];
  
  console.log(`🎯 Fetching all active cities for specialty URLs...`);
  
  // Fetch all active cities from the cities table
  const { data: cities, error } = await supabase
    .from('cities')
    .select('name, slug, country')
    .eq('is_active', true)
    .order('name');
  
  if (error) {
    console.error('❌ Error fetching cities:', error);
    return urls;
  }
  
  console.log(`🎯 Generating English specialty sub-landing URLs for ${cities?.length || 0} cities × ${citySpecialties.length} specialties...`);
  
  let generatedCount = 0;
  
  cities?.forEach((city) => {
    citySpecialties.forEach((specialty) => {
      urls.push({
        loc: `${BASE_URL}/city/${city.slug}/${specialty.slugs.en}`,
        lastmod: today,
        changefreq: 'weekly' as const,
        priority: '0.6',
        images: [{
          loc: `${BASE_URL}/images/specialties/${specialty.slugs.en}.webp`,
          title: `${specialty.names.en} in ${city.name}, ${city.country}`
        }]
      });
      
      generatedCount++;
      
      // Progress log every 50,000 URLs
      if (generatedCount % 50000 === 0) {
        console.log(`   Progress: ${generatedCount.toLocaleString()} English specialty URLs generated...`);
      }
    });
  });
  
  console.log(`✅ Generated ${urls.length.toLocaleString()} English specialty sub-landing URLs`);
  return urls;
};

/**
 * Generate translated specialty sub-landing pages from city_translations table
 */
const generateTranslatedSpecialtyURLs = async (): Promise<SitemapURL[]> => {
  const today = new Date().toISOString().split('T')[0];
  const urls: SitemapURL[] = [];
  const translations = await getCityTranslationsFromDB();
  
  console.log(`🌐 Generating translated specialty sub-landing URLs for ${translations.length} city translations × ${citySpecialties.length} specialties...`);
  
  let generatedCount = 0;
  
  translations.forEach(({ city_slug, language }) => {
    if (language === 'en') return; // Skip English, handled by generateEnglishSpecialtyURLs
    
    const city = worldCities.find(c => c.id === city_slug);
    if (!city) return;
    
    citySpecialties.forEach((specialty) => {
      // Get the specialty slug for the current language, fallback to English
      const specialtySlug = specialty.slugs[language as keyof typeof specialty.slugs] || specialty.slugs.en;
      const specialtyName = specialty.names[language as keyof typeof specialty.names] || specialty.names.en;
      
      const url = `${BASE_URL}/${language}/city/${city_slug}/${specialtySlug}`;
      
      urls.push({
        loc: url,
        lastmod: today,
        changefreq: 'weekly' as const,
        priority: '0.5',
        images: [{
          loc: `${BASE_URL}/images/specialties/${specialty.slugs.en}.webp`,
          title: `${specialtyName} in ${city.name}`
        }]
      });
      
      generatedCount++;
      
      // Progress log every 50,000 URLs
      if (generatedCount % 50000 === 0) {
        console.log(`   Progress: ${generatedCount.toLocaleString()} translated specialty URLs generated...`);
      }
    });
  });
  
  console.log(`✅ Generated ${urls.length.toLocaleString()} translated specialty sub-landing URLs`);
  return urls;
};

/**
 * Generate country landing pages URLs
 */
const generateCountryLandingURLs = (): SitemapURL[] => {
  const today = new Date().toISOString().split('T')[0];
  const countriesMap = new Map<string, { name: string; code: string }>();
  
  worldCities.forEach(city => {
    if (!countriesMap.has(city.countryCode)) {
      countriesMap.set(city.countryCode, {
        name: city.country,
        code: city.countryCode
      });
    }
  });

  return Array.from(countriesMap.values()).map(country => ({
    loc: `${BASE_URL}/country/${country.code.toLowerCase()}`,
    lastmod: today,
    changefreq: 'monthly' as const,
    priority: '0.7'
  }));
};

/**
 * Generate specialty category pages URLs
 */
const generateSpecialtyCategoryURLs = (): SitemapURL[] => {
  const today = new Date().toISOString().split('T')[0];

  return citySpecialties.map(specialty => ({
    loc: `${BASE_URL}/specialty/${specialty.slugs.en}`,
    lastmod: today,
    changefreq: 'monthly' as const,
    priority: '0.8',
    images: [{
      loc: `${BASE_URL}/images/specialties/${specialty.slugs.en}.webp`,
      title: specialty.names.en,
      caption: `Find ${specialty.names.en.toLowerCase()} specialists worldwide`
    }]
  }));
};

/**
 * Generate the complete sitemap with multilingual support
 * Includes 32,720 cities in English + translated cities + all specialty sub-landings
 */
export const generateCompleteSitemap = async (): Promise<SitemapURL[]> => {
  console.log('\n🚀 Starting comprehensive multilingual sitemap generation...\n');
  console.log('📋 Phase 1-3: Generating 32,720 English cities + 654,400 specialty sub-landings\n');
  
  const startTime = Date.now();
  
  // Generate all URL types in parallel for efficiency
  const [
    staticPages,
    englishCityLandings,          // NEW: 32,720 cities from cities table
    englishSpecialtySubLandings,  // NEW: 654,400 specialty URLs (32,720 × 20)
    translatedCityLandings,       // Existing translations
    translatedSpecialtySubLandings, // Existing specialty translations
    countryLandings,
    specialtyCategories
  ] = await Promise.all([
    Promise.resolve(generateStaticPages()),
    generateEnglishCityLandingURLs(),         // Read from cities table
    generateEnglishSpecialtyURLs(),           // Generate 32,720 × 20
    generateTranslatedCityLandingURLs(),      // Read from city_translations
    generateTranslatedSpecialtyURLs(),        // Read from city_translations
    Promise.resolve(generateCountryLandingURLs()),
    Promise.resolve(generateSpecialtyCategoryURLs())
  ]);

  const allURLs = [
    ...staticPages,
    ...englishCityLandings,
    ...englishSpecialtySubLandings,
    ...translatedCityLandings,
    ...translatedSpecialtySubLandings,
    ...countryLandings,
    ...specialtyCategories,
  ];
  
  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(2);

  console.log(`\n📊 Sitemap Generation Complete in ${elapsedTime}s:`);
  console.log(`\n🇬🇧 ENGLISH PAGES (Phase 1-3):`);
  console.log(`   ✓ City landings (EN): ${englishCityLandings.length.toLocaleString()}`);
  console.log(`   ✓ Specialty sub-landings (EN): ${englishSpecialtySubLandings.length.toLocaleString()}`);
  console.log(`   📊 Subtotal English: ${(englishCityLandings.length + englishSpecialtySubLandings.length).toLocaleString()}`);
  console.log(`\n🌍 TRANSLATED PAGES:`);
  console.log(`   ✓ City landings (Translated): ${translatedCityLandings.length.toLocaleString()}`);
  console.log(`   ✓ Specialty sub-landings (Translated): ${translatedSpecialtySubLandings.length.toLocaleString()}`);
  console.log(`\n📄 OTHER PAGES:`);
  console.log(`   ✓ Static pages: ${staticPages.length.toLocaleString()}`);
  console.log(`   ✓ Country landings: ${countryLandings.length.toLocaleString()}`);
  console.log(`   ✓ Specialty categories: ${specialtyCategories.length.toLocaleString()}`);
  console.log(`\n🎯 TOTAL URLs: ${allURLs.length.toLocaleString()}`);
  console.log(`   💾 Estimated sitemap size: ${((allURLs.length * 500) / 1024 / 1024).toFixed(2)} MB\n`);

  return allURLs;
};

/**
 * Convert sitemap to XML format
 */
export const generateSitemapXML = (urls: SitemapURL[]): string => {
  const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>\n';
  const urlsetOpen = '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"\n' +
    '        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1"\n' +
    '        xmlns:video="http://www.google.com/schemas/sitemap-video/1.1">\n';
  const urlsetClose = '</urlset>';

  const urlEntries = urls.map(url => {
    let entry = '  <url>\n';
    entry += `    <loc>${escapeXML(url.loc)}</loc>\n`;
    
    if (url.lastmod) {
      entry += `    <lastmod>${url.lastmod}</lastmod>\n`;
    }
    
    if (url.changefreq) {
      entry += `    <changefreq>${url.changefreq}</changefreq>\n`;
    }
    
    if (url.priority !== undefined) {
      entry += `    <priority>${url.priority}</priority>\n`;
    }

    // Add images
    if (url.images && url.images.length > 0) {
      url.images.forEach(image => {
        entry += '    <image:image>\n';
        entry += `      <image:loc>${escapeXML(image.loc)}</image:loc>\n`;
        if (image.title) {
          entry += `      <image:title>${escapeXML(image.title)}</image:title>\n`;
        }
        if (image.caption) {
          entry += `      <image:caption>${escapeXML(image.caption)}</image:caption>\n`;
        }
        if (image.geoLocation) {
          entry += `      <image:geo_location>${escapeXML(image.geoLocation)}</image:geo_location>\n`;
        }
        entry += '    </image:image>\n';
      });
    }

    // Add videos
    if (url.videos && url.videos.length > 0) {
      url.videos.forEach(video => {
        entry += '    <video:video>\n';
        entry += `      <video:thumbnail_loc>${escapeXML(video.thumbnailLoc)}</video:thumbnail_loc>\n`;
        entry += `      <video:title>${escapeXML(video.title)}</video:title>\n`;
        entry += `      <video:description>${escapeXML(video.description)}</video:description>\n`;
        if (video.contentLoc) {
          entry += `      <video:content_loc>${escapeXML(video.contentLoc)}</video:content_loc>\n`;
        }
        if (video.playerLoc) {
          entry += `      <video:player_loc>${escapeXML(video.playerLoc)}</video:player_loc>\n`;
        }
        if (video.duration) {
          entry += `      <video:duration>${video.duration}</video:duration>\n`;
        }
        entry += '    </video:video>\n';
      });
    }

    entry += '  </url>\n';
    return entry;
  }).join('');

  return xmlHeader + urlsetOpen + urlEntries + urlsetClose;
};

/**
 * Escape XML special characters
 */
const escapeXML = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Split sitemap into multiple files (max 50,000 URLs per file)
 */
export const generateSitemapIndex = (
  sitemaps: { filename: string; lastmod: string; urlCount: number }[]
): string => {
  const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>\n';
  const sitemapIndexOpen = '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
  const sitemapIndexClose = '</sitemapindex>';

  const sitemapEntries = sitemaps.map(sitemap => {
    let entry = '  <sitemap>\n';
    entry += `    <loc>${BASE_URL}/${sitemap.filename}</loc>\n`;
    entry += `    <lastmod>${sitemap.lastmod}</lastmod>\n`;
    entry += '  </sitemap>\n';
    return entry;
  }).join('');

  return xmlHeader + sitemapIndexOpen + sitemapEntries + sitemapIndexClose;
};

/**
 * Split URLs into multiple sitemaps (max 50,000 per file)
 */
export const splitIntoMultipleSitemaps = (
  urls: SitemapURL[],
  maxURLsPerFile: number = 50000
): { urls: SitemapURL[]; filename: string }[] => {
  const sitemaps: { urls: SitemapURL[]; filename: string }[] = [];
  
  for (let i = 0; i < urls.length; i += maxURLsPerFile) {
    const chunk = urls.slice(i, i + maxURLsPerFile);
    const fileNumber = Math.floor(i / maxURLsPerFile) + 1;
    sitemaps.push({
      urls: chunk,
      filename: `sitemap-${fileNumber}.xml`
    });
  }

  return sitemaps;
};

/**
 * Calculate comprehensive sitemap statistics from database
 * Includes both English cities from cities table and translated cities
 */
export const calculateSitemapStats = async (): Promise<SitemapStats> => {
  console.log('📊 Calculating sitemap statistics from database...');
  
  // Get count of active cities from cities table (English)
  const { count: englishCitiesCount } = await supabase
    .from('cities')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true);
  
  // Get count of translated cities from city_translations (excluding English)
  const { count: translationsCount } = await supabase
    .from('city_translations')
    .select('*', { count: 'exact', head: true })
    .neq('language', 'en');
  
  const englishCities = englishCitiesCount || 0;
  const translatedCities = translationsCount || 0;
  const totalCityLandings = englishCities + translatedCities;
  
  const englishSpecialtyLandings = englishCities * citySpecialties.length;
  const translatedSpecialtyLandings = translatedCities * citySpecialties.length;
  const totalSpecialtyLandings = englishSpecialtyLandings + translatedSpecialtyLandings;
  
  const staticPages = generateStaticPages();
  const countries = generateCountryLandingURLs();
  const specialties = generateSpecialtyCategoryURLs();
  
  const staticPagesCount = staticPages.length + countries.length + specialties.length;
  const totalURLs = totalCityLandings + totalSpecialtyLandings + staticPagesCount;
  const sitemapSize = totalURLs * 500; // Approximate 500 bytes per URL

  console.log(`✅ Statistics calculated:`);
  console.log(`   🇬🇧 English cities: ${englishCities.toLocaleString()}`);
  console.log(`   🇬🇧 English specialty URLs: ${englishSpecialtyLandings.toLocaleString()}`);
  console.log(`   🌍 Translated cities: ${translatedCities.toLocaleString()}`);
  console.log(`   🌍 Translated specialty URLs: ${translatedSpecialtyLandings.toLocaleString()}`);
  console.log(`   🎯 Total URLs: ${totalURLs.toLocaleString()}`);

  return {
    totalURLs,
    cityLandings: totalCityLandings,
    specialtyLandings: totalSpecialtyLandings,
    staticPages: staticPagesCount,
    sitemapSize,
    estimatedIndexablePages: Math.floor(totalURLs * 0.85),
  };
};

/**
 * Generate language-specific sitemaps for better organization
 */
export const generateSitemapsByLanguage = async (): Promise<Map<string, SitemapURL[]>> => {
  console.log('🌐 Generating language-specific sitemaps...');
  
  const allUrls = await generateCompleteSitemap();
  const sitemapsByLang = new Map<string, SitemapURL[]>();
  
  allUrls.forEach(url => {
    // Detect language from URL pattern
    const langMatch = url.loc.match(/\.help\/([a-z]{2})\//);
    const lang = langMatch ? langMatch[1] : 'en';
    
    if (!sitemapsByLang.has(lang)) {
      sitemapsByLang.set(lang, []);
    }
    sitemapsByLang.get(lang)!.push(url);
  });
  
  console.log(`✅ Created ${sitemapsByLang.size} language-specific sitemaps:`);
  sitemapsByLang.forEach((urls, lang) => {
    console.log(`   - ${lang}: ${urls.length.toLocaleString()} URLs`);
  });
  
  return sitemapsByLang;
};

/**
 * Generate multilingual sitemap index
 */
export const generateMultiLanguageSitemapIndex = (
  languageSitemaps: Map<string, number>
): string => {
  const today = new Date().toISOString().split('T')[0];
  
  let index = '<?xml version="1.0" encoding="UTF-8"?>\n';
  index += '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
  
  languageSitemaps.forEach((count, lang) => {
    index += '  <sitemap>\n';
    index += `    <loc>${BASE_URL}/sitemap-${lang}.xml</loc>\n`;
    index += `    <lastmod>${today}</lastmod>\n`;
    index += `    <!-- ${count.toLocaleString()} URLs -->\n`;
    index += '  </sitemap>\n';
  });
  
  index += '</sitemapindex>';
  return index;
};

/**
 * Validate sitemap compliance
 */
export const validateSitemap = (urls: SitemapURL[]): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check URL count
  if (urls.length > 50000) {
    errors.push(`Sitemap exceeds 50,000 URL limit (${urls.length.toLocaleString()} URLs). Please split into multiple sitemaps.`);
  }

  // Check URL format (sample first 1000 for performance)
  const sampleSize = Math.min(urls.length, 1000);
  for (let i = 0; i < sampleSize; i++) {
    const url = urls[i];
    
    if (!url.loc.startsWith('http')) {
      errors.push(`URL ${i + 1}: Invalid protocol (must be http or https)`);
    }

    if (url.loc.length > 2048) {
      errors.push(`URL ${i + 1}: URL exceeds 2048 character limit`);
    }

    if (url.priority !== undefined) {
      const priority = parseFloat(url.priority);
      if (priority < 0 || priority > 1) {
        warnings.push(`URL ${i + 1}: Priority should be between 0.0 and 1.0`);
      }
    }
  }

  if (urls.length > sampleSize) {
    warnings.push(`Only first ${sampleSize} URLs were validated for performance reasons.`);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Generate robots.txt content
 */
export const generateRobotsTxt = (): string => {
  return `# Global Therapy Hub - Robots.txt
# Generated: ${new Date().toISOString()}

User-agent: *
Allow: /

# Sitemaps
Sitemap: ${BASE_URL}/sitemap.xml
Sitemap: ${BASE_URL}/sitemap-index.xml

# Crawl-delay for specific bots
User-agent: Googlebot
Crawl-delay: 0

User-agent: Bingbot
Crawl-delay: 1

# Block admin areas
Disallow: /admin/
Disallow: /api/
Disallow: /private/

# Block search and filter params
Disallow: /*?sort=*
Disallow: /*?filter=*
Disallow: /*?page=*

# Block duplicate content
Disallow: /*?utm_*
Disallow: /*?ref=*
Disallow: /*?fbclid=*
`;
};

export default {
  generateCompleteSitemap,
  generateSitemapXML,
  generateSitemapIndex,
  splitIntoMultipleSitemaps,
  calculateSitemapStats,
  validateSitemap,
  generateRobotsTxt,
  generateSitemapsByLanguage,
  generateMultiLanguageSitemapIndex
};
